/** Distance from the end of the history, in pixels, that still counts as its end. */
export const CHAT_END_TOLERANCE = 2;

export interface ChatScrollStep {
  /** Scroll offset before this step. */
  previousTop: number;
  /** Scroll offset after this step. */
  top: number;
  /** What is left below the visible part after this step. */
  distanceFromEnd: number;
}

/**
 * Whether the history keeps the newest message in sight after a scroll step.
 *
 * Arriving at the end turns following on. A step up that leaves the end turns
 * it off, so a reader is not pulled along while an answer is still being
 * written. A step down that stops short of the end changes nothing: that is
 * the history itself moving towards the end, or a reader on the way there.
 */
/** What a jump to the end needs from the history it moves. */
export interface EndJumpHost {
  /** Puts the history at its end and stops any motion on it. */
  toEnd(): void;
  /** Whether the reader still keeps the newest message in sight. */
  follows(): boolean;
  /** Resolves when the pending render is in the page. */
  rendered(): Promise<unknown>;
  /** Runs a step with the next frame. */
  nextFrame(step: () => void): void;
}

/**
 * A jump to the end of the history, without animation.
 *
 * It takes several steps, because messages are still being laid out when it
 * starts. From its first step to its last it owns the scroll position: a
 * follow step started in between would animate from a position the jump has
 * already left and pull the history back up. A reader who leaves the end
 * while it runs is not moved again.
 */
export class EndJump {
  private running = 0;

  /** Whether a jump is under way. */
  get active(): boolean {
    return this.running > 0;
  }

  start(host: EndJumpHost): void {
    this.running++;
    const step = () => {
      if (host.follows()) host.toEnd();
    };
    step();
    host
      .rendered()
      .then(() => {
        step();
        host.nextFrame(() => {
          step();
          host.nextFrame(() => {
            step();
            this.running--;
          });
        });
      })
      .catch(error => {
        this.running--;
        console.error(error);
      });
  }
}

export function followsAfterScroll(
  following: boolean,
  step: ChatScrollStep
): boolean {
  if (step.distanceFromEnd <= CHAT_END_TOLERANCE) return true;
  if (step.top < step.previousTop - 1) return false;
  return following;
}
